import { useState, useRef, useCallback, useEffect, type ReactNode } from "react";

interface PullToRefreshProps {
  onRefresh: () => Promise<void>;
  children: ReactNode;
}

const THRESHOLD = 60;
const MAX_PULL = 100;
const RESISTANCE = 2.5;

function PullToRefresh({ onRefresh, children }: PullToRefreshProps) {
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [thresholdCrossed, setThresholdCrossed] = useState(false);
  const [isTouching, setIsTouching] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const startY = useRef(0);
  const isPulling = useRef(false);
  const hasTriggeredHaptic = useRef(false);

  // Trigger haptic when threshold is crossed
  useEffect(() => {
    if (thresholdCrossed && !hasTriggeredHaptic.current && !isRefreshing) {
      hasTriggeredHaptic.current = true;
      if (window.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.impactOccurred("medium");
      }
    }
    if (!thresholdCrossed) {
      hasTriggeredHaptic.current = false;
    }
  }, [thresholdCrossed, isRefreshing]);

  // Check if at top of scroll (works with both container and window scroll)
  const isAtTop = useCallback(() => {
    const container = containerRef.current;
    // Check window scroll first (tab system uses window scroll)
    if (window.scrollY > 0) return false;
    // Also check container scroll if it has its own scrollbar
    if (container && container.scrollTop > 0) return false;
    return true;
  }, []);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (isRefreshing) return;

    const touch = e.touches[0];
    if (!touch) return;

    // Only start pulling if we're at the very top
    if (isAtTop()) {
      startY.current = touch.clientY;
      isPulling.current = true;
      setIsTouching(true);
    }
  }, [isRefreshing, isAtTop]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isPulling.current || isRefreshing) return;

    // If user scrolled down, cancel the pull
    if (!isAtTop()) {
      isPulling.current = false;
      setPullDistance(0);
      setThresholdCrossed(false);
      setIsTouching(false);
      return;
    }

    const touch = e.touches[0];
    if (!touch) return;

    const diff = touch.clientY - startY.current;

    if (diff > 0) {
      const distance = Math.min(diff / RESISTANCE, MAX_PULL);
      setPullDistance(distance);
      setThresholdCrossed(distance >= THRESHOLD);

      if (distance > 5) {
        e.preventDefault();
      }
    } else {
      setPullDistance(0);
      setThresholdCrossed(false);
    }
  }, [isRefreshing]);

  const handleTouchEnd = useCallback(async () => {
    if (!isPulling.current) return;
    isPulling.current = false;
    setIsTouching(false);

    if (thresholdCrossed && !isRefreshing) {
      setIsRefreshing(true);
      setPullDistance(THRESHOLD);

      try {
        await onRefresh();
      } finally {
        setIsRefreshing(false);
        setPullDistance(0);
        setThresholdCrossed(false);
      }
    } else {
      setPullDistance(0);
      setThresholdCrossed(false);
    }
  }, [thresholdCrossed, isRefreshing, onRefresh]);

  // Progress from 0 to 1
  const progress = Math.min(pullDistance / THRESHOLD, 1);

  // Arc grows from ~20% to 75% (not 100%, so spinning is visible)
  // During refresh, keep at 75% so you can see the rotation
  const arcProgress = isRefreshing ? 0.75 : 0.2 + (progress * 0.55);

  // Rotation during pull (not during refresh)
  const pullRotation = progress * 360;

  // Content offset
  const contentOffset = Math.min(pullDistance, MAX_PULL);

  // Show spinner when pulling or refreshing
  const showSpinner = pullDistance > 8 || isRefreshing;

  return (
    <>
      {/* Spinner - rendered outside container to avoid clipping */}
      <div
        className="ptr-spinner-area"
        style={{
          opacity: showSpinner ? 1 : 0,
        }}
      >
        <div
          className={`ptr-spinner-wrapper ${isRefreshing ? "refreshing" : ""}`}
          style={{
            transform: isRefreshing ? undefined : `rotate(${pullRotation}deg)`,
          }}
        >
          <svg viewBox="0 0 24 24" className="ptr-svg">
            {/* Track (subtle) */}
            <circle
              cx="12"
              cy="12"
              r="9"
              fill="none"
              strokeWidth="2.5"
              className="ptr-track"
            />
            {/* Progress arc */}
            <circle
              cx="12"
              cy="12"
              r="9"
              fill="none"
              strokeWidth="2.5"
              className="ptr-arc"
              strokeDasharray={`${arcProgress * 56.5} 56.5`}
              strokeLinecap="round"
            />
          </svg>
        </div>
      </div>

      {/* Scrollable container */}
      <div
        ref={containerRef}
        className="ptr-container"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Content */}
        <div
          className="ptr-content"
          style={{
            transform: contentOffset > 0 ? `translateY(${contentOffset}px)` : undefined,
            transition: isTouching ? 'none' : 'transform 0.25s ease-out',
          }}
        >
          {children}
        </div>
      </div>
    </>
  );
}

export default PullToRefresh;
